import { useAuth0 } from "@auth0/auth0-react";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { Checkbox } from "../../../components/Checkbox/Checkbox.tsx";
import { ErrorWrapper } from "../../../components/Error/ErrorWrapper.tsx";
import { PhoneField } from "../../../components/PhoneField/PhoneField.tsx";
import { RadioButton } from "../../../components/RadioButton/RadioButton.tsx";
import { TextField } from "../../../components/TextField/TextField.tsx";
import { useUpdateUser } from "../../Home/HomeIndex/hooks/useUpdateUser.ts";
import { ABOUT_YOU_STEP, TEAM_SIZE_OPTIONS } from "../constants.ts";
import type { UserInformation } from "../types.ts";
import { computeUserInformationStepProgress } from "../utils/computeUserInformationStepProgress.ts";
import { UserOnboardingFormLayout } from "./UserOnboardingFormLayout.tsx";

export const UserInformationsOnboardingForm = ({
  onComplete,
}: {
  onComplete: () => void;
}) => {
  const { updateUser, isLoading: isUserUpdateLoading } = useUpdateUser();
  const [input, setInput] = useState<UserInformation>({
    firstName: "",
    lastName: "",
    phoneNumber: { prefix: undefined, number: undefined },
    teamSize: null,
    isCreativeAgency: false,
    companyWebsite: null,
  });

  const [errors, setErrors] = useState<{
    firstName: string[];
    lastName: string[];
    phoneNumber: string[];
    teamSize: string[];
  }>({
    firstName: [],
    lastName: [],
    phoneNumber: [],
    teamSize: [],
  });

  const [shouldDisplayErrors, setShouldDisplayErrors] = useState(false);

  const areAnyErrors = Object.values(errors).some((error) => error.length > 0);

  // FIXME: This should be done in the backend during `login` mutation by direct auth0 http call:
  //  https://auth0.com/docs/api/authentication#get-user-info
  const { user: auth0User } = useAuth0();

  useEffect(
    () => {
      if (auth0User?.given_name && auth0User.family_name) {
        setInput({
          ...input,
          firstName: auth0User.given_name,
          lastName: auth0User.family_name,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth0User?.given_name, auth0User?.family_name],
  );

  const isFirstNameEditable = auth0User?.given_name === undefined;
  const isLastNameEditable = auth0User?.family_name === undefined;

  useEffect(() => {
    setErrors({
      firstName: input.firstName ? [] : ["First name is required"],
      lastName: input.lastName ? [] : ["Last name is required"],
      phoneNumber:
        input.phoneNumber.number === undefined &&
        input.phoneNumber.prefix === undefined
          ? ["Please input a country code", "Please input a phone number"]
          : input.phoneNumber.prefix === undefined
          ? ["Please input a country code"]
          : input.phoneNumber.number === ""
          ? ["Please input a phone number"]
          : [],
      teamSize: input.teamSize ? [] : ["Please select a team size"],
    });
  }, [input]);

  return (
    <UserOnboardingFormLayout
      title="Hello"
      step={ABOUT_YOU_STEP}
      progress={computeUserInformationStepProgress(input)}
      onContinue={() => {
        if (!areAnyErrors) {
          updateUser({
            first_name: input.firstName,
            last_name: input.lastName,
            phone_number: `${input.phoneNumber.prefix ?? ""}${
              input.phoneNumber.number ?? ""
            }`,
            company_size: input.teamSize,
            company_website: input.companyWebsite,
            has_worked_recently_with_creative_agency: input.isCreativeAgency,
          });
          onComplete();
        }
        setShouldDisplayErrors(true);
      }}
      isLoading={isUserUpdateLoading}
    >
      <div className="flex-col gap-400 w-[340px]">
        <div className="grid grid-cols-2 gap-100">
          <TextField
            disabled={!isFirstNameEditable}
            label="First name*"
            size="md"
            value={input.firstName}
            onChange={(firstName) => setInput({ ...input, firstName })}
            placeholder="John"
            errors={shouldDisplayErrors ? errors.firstName : []}
          />
          <TextField
            disabled={!isLastNameEditable}
            label="Last name*"
            size="md"
            value={input.lastName}
            onChange={(lastName) => setInput({ ...input, lastName })}
            placeholder="Doe"
            errors={shouldDisplayErrors ? errors.lastName : []}
          />
        </div>
        {/* FIXME: Design of the phone field should be improved to be more discoverable for users (TK-3279)*/}
        <PhoneField
          label="Phone number*"
          value={input.phoneNumber}
          onChange={(phoneNumber) => setInput({ ...input, phoneNumber })}
          placeholder="XXXXXXXXX"
          errors={shouldDisplayErrors ? errors.phoneNumber : []}
        />
        <div className="flex-col gap-100">
          <div className="body-md-semibold text-primary">Team size*</div>
          <ErrorWrapper errors={shouldDisplayErrors ? errors.teamSize : []}>
            <div
              className={classNames(
                "grid grid-cols-4 px-100 py-150 rounded-150",
                shouldDisplayErrors &&
                  errors.teamSize.length > 0 &&
                  "outline-error",
              )}
            >
              {TEAM_SIZE_OPTIONS.map((option) => (
                <RadioButton
                  key={option}
                  label={option}
                  checked={input.teamSize === option}
                  onChange={() => {
                    setInput({ ...input, teamSize: option });
                  }}
                />
              ))}
            </div>
          </ErrorWrapper>
        </div>
        <div className="h-025 w-full border-t" />
        <TextField
          label="Company website"
          size="md"
          value={input.companyWebsite ?? ""}
          onChange={(companyWebsite) =>
            setInput({
              ...input,
              companyWebsite:
                companyWebsite.trim() === "" ? null : companyWebsite,
            })
          }
          placeholder="https://example.com"
        />
        <Checkbox
          label="I work in a creative agency or recently worked with one"
          checked={input.isCreativeAgency}
          onChange={(isCreativeAgency) =>
            setInput({ ...input, isCreativeAgency })
          }
        />
      </div>
    </UserOnboardingFormLayout>
  );
};
